import type { SignupFlow, StringMap } from 'corgi-types';
import type { ThirdPartyProvider } from 'graphql-ts-types';
import { AuthenticationProvider } from 'graphql-ts-types';

import { shouldAddUserFlowQueryParam } from 'common/utils/signup';
import { getQueryParamsString } from 'common/utils/url';

interface GetRegistrationUrlArgs {
  userFlow: SignupFlow;
  providerToken: string;
  provider: ThirdPartyProvider;
  prefix: (path: string) => string;
  providerData?: StringMap;
  referredBy?: string;
}

export const isUserNewUser = (existentAssociations: AuthenticationProvider[]) =>
  existentAssociations.length === 0;

const isUserRegularUser = (existentAssociations: AuthenticationProvider[]) =>
  existentAssociations.includes(AuthenticationProvider.Home24Password);

export const isUserOnlyRegularUser = (existentAssociations: AuthenticationProvider[]) =>
  existentAssociations.length === 1 &&
  existentAssociations[0] === AuthenticationProvider.Home24Password;

const isUserThirdPartyUser = (existentAssociations: AuthenticationProvider[]) =>
  existentAssociations.includes(AuthenticationProvider.Apple) ||
  existentAssociations.includes(AuthenticationProvider.Google);

export const isUserOnlyThirdPartyUser = (existentAssociations: AuthenticationProvider[]) =>
  existentAssociations.length === 1 && isUserThirdPartyUser(existentAssociations);

export const isUserRegularAndThirdPartyUser = (existentAssociations: AuthenticationProvider[]) =>
  isUserRegularUser(existentAssociations) && isUserThirdPartyUser(existentAssociations);

export const getRegistrationUrl = ({
  prefix,
  userFlow,
  provider,
  providerToken,
  providerData,
  referredBy,
}: GetRegistrationUrlArgs) => {
  const baseUrl = prefix('/registration');
  const queryParams = getQueryParamsString({
    provider,
    providerToken,
    ...(!!providerData ? { providerData: JSON.stringify(providerData) } : {}),
    ...(shouldAddUserFlowQueryParam(userFlow) ? { userFlow } : {}),
    ...(referredBy && { code: referredBy }),
  });

  return `${baseUrl}?${queryParams}`;
};

export const getEmailFromJwtToken = async (token: string) => {
  const { default: jwtDecode } = await import('jwt-decode');

  const {
    email,
  }: {
    email: string;
  } = jwtDecode(token);

  return email;
};
